import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import CoachLandingHeader from "../components/coach/CoachLandingHeader";
import CoachLandingCierre from "../components/coach/CoachLandingCierre";
import Footer from "../components/Footer";
import { trackCoachCtaClick } from "../analytics/googleAnalytics";
import {
  HARBIZ_CONSULTADO,
  HARBIZ_PLANES,
  HARBIZ_EXTRAS,
  CASOS,
  DONDE_GANA_HARBIZ,
  DONDE_GANA_WAYO,
  PREGUNTAS,
} from "../data/harbizComparativa";
import "../components/coach/coach-landing.css";

/**
 * /alternativa-harbiz — para quien busca cambiarse de Harbiz.
 *
 * La idea viene de bsport, que puja en Google por «harbiz» y lleva a una
 * comparativa propia (pro.bsport.io/es/comparison-harbiz). Aquí, gratis, por SEO.
 * Los dos posts del blog sobre Harbiz responden a «precio y opiniones»; esta
 * página es la de «me quiero cambiar».
 *
 * Reconoce lo que Harbiz hace mejor (reservas, cobros, app de marca) y lo que
 * pagan los clientes en Wayo: es lo que la hace creíble y lo que evita que se
 * registre quien necesita reservas. Datos en src/data/harbizComparativa.js.
 */
export default function AlternativaHarbiz() {

  return (
    <>
      <Helmet>
        <title>Alternativa a Harbiz Gratis | Wayo Coach</title>
        <meta
          name="description"
          content="Harbiz cuesta de 14 a 199 € al mes y limita tus clientes. Wayo Coach es gratis y sin límite de clientes. Comparativa honesta: precios, qué hace mejor cada uno y cómo cambiarte."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://wayoai.app/alternativa-harbiz" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wayoai.app/alternativa-harbiz" />
        <meta property="og:title" content="Alternativa a Harbiz gratis: Wayo Coach" />
        <meta
          property="og:description"
          content="Clientes, rutinas, nutrición con IA y seguimiento, sin cuota y sin límite de clientes."
        />
        <meta property="og:image" content="https://wayoai.app/assets/img/og-coaches.jpg" />
        {/* El FAQPage lo mete el prerender en el HTML (scripts/prerender-meta.js,
            con las mismas PREGUNTAS). Repetirlo aquí lo duplicaría al cargar. */}
      </Helmet>

      <CoachLandingHeader />

      <section className="coach-landing-hero alt-hero">
        <div className="container">
          <div className="hero-badge">Alternativa a Harbiz</div>
          <h1>
            La alternativa a Harbiz
            <br />
            <span className="highlight-green">que no cobra al entrenador.</span>
          </h1>
          <p className="hero-subtitle">
            Harbiz es un buen software y cuesta de 14 a 199 € al mes según cuántos clientes lleves.
            Wayo Coach hace lo esencial (clientes, rutinas con vídeo, nutrición con IA y
            seguimiento) gratis y sin límite de clientes. Aquí tienes la comparativa, con lo que
            hace mejor cada uno.
          </p>
          <div className="hero-ctas">
            <Link
              to="/coach/register"
              className="btn-primary"
              onClick={() => trackCoachCtaClick("alternativa_harbiz_hero")}
            >
              Crear mi cuenta gratis →
            </Link>
            <a href="#comparativa" className="btn-secondary">
              Ver la comparativa
            </a>
          </div>
          <p className="hero-microcopy">Sin tarjeta · Sin permanencia · Sin límite de clientes</p>
        </div>
      </section>

      <section className="compare-section" id="comparativa">
        <div className="container">
          <div className="compare-header">
            <div className="section-tag">Lo que cuesta</div>
            <h2 className="section-title">Lo que pagas al año, según tus clientes</h2>
            <p className="section-subtitle">
              Tarifas de harbiz.io, consultadas el {HARBIZ_CONSULTADO}. Sin contar sus extras.
            </p>
          </div>
          <div className="compare-table">
            <div className="compare-row compare-header-row">
              <div className="compare-cell">Tus clientes</div>
              <div className="compare-cell">Harbiz</div>
              <div className="compare-cell">Wayo Coach</div>
            </div>
            {CASOS.map((c) => (
              <div key={c.clientes} className="compare-row">
                <div className="compare-cell">{c.clientes} clientes</div>
                <div className="compare-cell">{c.harbiz}</div>
                <div className="compare-cell">
                  <span className="compare-free-badge">0 €</span>
                </div>
              </div>
            ))}
            <div className="compare-row">
              <div className="compare-cell">Más de 50 clientes</div>
              <div className="compare-cell">Fuera de sus planes publicados</div>
              <div className="compare-cell">
                <span className="compare-free-badge">0 €</span>
              </div>
            </div>
          </div>
          <div className="alt-planes">
            <div>
              <strong>Planes de Harbiz</strong>
              <ul>
                {HARBIZ_PLANES.map((p) => (
                  <li key={p.plan}>
                    {p.plan}: {p.mensual} €/mes, o {p.anual} €/mes pagando el año · {p.clientes}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <strong>Sus extras de pago</strong>
              <ul>
                {HARBIZ_EXTRAS.map((e) => (
                  <li key={e.extra}>
                    {e.extra}: +{e.precio} €/mes
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="segmentos-section">
        <div className="container">
          <div className="segmentos-header">
            <div className="section-tag">Sin trampas</div>
            <h2 className="section-title">Qué hace mejor cada uno</h2>
            <p className="section-subtitle alt-subtitulo-claro">
              Si vives de las reservas de clases o necesitas cobrar dentro de la app, Wayo no es
              para ti. Si quieres llevar a tus clientes sin pagar una cuota cada mes, sí.
            </p>
          </div>
          <div className="segmentos-grid alt-dos">
            <div className="segmento-card">
              <h3>Harbiz es mejor si necesitas…</h3>
              <ul>
                {DONDE_GANA_HARBIZ.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
            <div className="segmento-card is-destacado">
              <h3>Wayo Coach es mejor si quieres…</h3>
              <ul>
                {DONDE_GANA_WAYO.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section alt-faq" id="faq">
        <div className="container">
          <div className="faq-header">
            <div className="section-tag">Preguntas</div>
            <h2 className="section-title">Antes de cambiarte</h2>
          </div>
          <div className="alt-faq-lista">
            {PREGUNTAS.map(([q, a]) => (
              <details key={q} className="alt-faq-item">
                <summary>{q}</summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
          <p className="alt-mas">
            Más detalle en{" "}
            <Link to="/blog-entrenadores/harbiz-precio-opiniones-alternativas">
              Harbiz: precio, opiniones y alternativas
            </Link>{" "}
            y todo lo que hace el panel en <Link to="/coaches">Wayo Coach</Link>.
          </p>
        </div>
      </section>

      <CoachLandingCierre />
      <Footer />
    </>
  );
}
